import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { deviceApi } from '@/api/production/devices';
import { DeviceTable } from '@/components/tables/production/device-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';

export function DeviceListPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['devices', page, search],
    queryFn: () => deviceApi.findMany({ page, serial: search || undefined }),
  });

  const deleteMutation = useMutation({
    mutationFn: (deviceId: string) => deviceApi.delete(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Equipamento excluído com sucesso!',
      });
    },
    onError: () => {
      toast({
        title: 'Erro!',
        description: 'Falha ao excluir o equipamento!',
        variant: 'destructive',
      });
    },
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page on new search
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  const devices = data?.docs || [];
  const totalPages = data?.pages || 0;
  const total = data?.total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <div>
          <h1 className="text-base font-semibold leading-7 text-gray-900">
            Gestão de dispositivos
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Uma lista de todos os dispositivos registrados na sua conta.
          </p>
        </div>

        <Link
          to="/production/devices/create"
          className="ml-auto flex items-center gap-x-1 rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
        >
          <Plus className="-ml-1.5 h-5 w-5" aria-hidden="true" />
          Novo dispositivo
        </Link>
      </div>

      {/* Search */}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex gap-2 max-w-md">
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder="Buscar por serial..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              className="pr-10"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          <Button onClick={handleSearch} className="bg-black hover:bg-gray-800 text-white">
            Pesquisar
          </Button>
          {search && (
            <Button variant="outline" onClick={handleClearSearch}>
              Limpar
            </Button>
          )}
        </div>
        {search && (
          <p className="mt-2 text-sm text-gray-600">
            {total} {total === 1 ? 'resultado encontrado' : 'resultados encontrados'}
          </p>
        )}
      </div>

      {/* Table */}
      <div className="px-4 sm:px-6 lg:px-8">
        <DeviceTable
          data={devices}
          onDelete={(deviceId) => deleteMutation.mutate(deviceId)}
          isDeleting={deleteMutation.isPending}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex-1 flex justify-between sm:hidden">
            <Button variant="outline" onClick={() => setPage(page - 1)} disabled={page === 1}>
              Anterior
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
            >
              Próximo
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando página <span className="font-medium">{page}</span> de{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <Button
                  variant="outline"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-l-md"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber: number;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (page <= 3) {
                    pageNumber = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = page - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNumber}
                      variant={page === pageNumber ? 'default' : 'outline'}
                      onClick={() => setPage(pageNumber)}
                      className={page === pageNumber ? 'bg-black hover:bg-gray-800' : ''}
                    >
                      {pageNumber}
                    </Button>
                  );
                })}

                <Button
                  variant="outline"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  className="rounded-r-md"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
